// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Col, Layout, Row} from "antd";
import OrganizationTree from "../../components/user/OrganizationTree";
import UserListTable from "../../components/user/UserListTable";
import DepartmentEdit from "../../components/user/DepartmentEdit";
import OrganizationEditPage from "./OrganizationEditPage";
import UserEditPage from "./UserEditPage";
import * as OrganizationBackend from "../../services/OrganizationBackend";
import * as Setting from "../../conf/Setting";

const {Sider, Content} = Layout;

class UserManagementPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      treeData: [],
      expandedNodeKeys: [],
      selectedNodeKeys: [],
      selectedTreeNode: null,
      editDepartmentOpen: false,
      editDepartment: null,
      editOrganizationOpen: false,
      organizationId: undefined,
      organizationDisplayName: undefined,
      editUserOpen: false,
      editUser: null,
      editMode: "add",
      searchList: [],
      selectedSearch: null,
      keyword: "",
      editDeptParent: null,
    };
  }

  componentDidMount() {
    this.fetchTreeData(true);
  }

  fetchTreeData = (initial) => {
    OrganizationBackend.getOrganizations(Setting.Admin)
      .then((res) => {
        if (res.msg === undefined) {
          const treeList1 = res.filter(organization => !Setting.isAdminOrganization(organization.uid)).reverse();
          const treeList2 = res.filter(organization => Setting.isAdminOrganization(organization.uid));
          const treeList = [...treeList1, ...treeList2];

          treeList.map((item, index) => {
            item.isOrganization = true;
            item.level = 0;
            this.state.treeData.map(node => {
              if (node.uid === item.uid) {
                item.children = node.children;
                return;
              }
            });
          });

          this.setState({
            treeData: treeList,
          });
          // 初始获取treeData
          if (this.state.treeData.length === 0 && treeList.length > 0) {
            this.setState({
              selectedNodeKeys: [treeList[0].uid],
              expandedNodeKeys: [treeList[0].uid],
              selectedTreeNode: treeList[0],
            });
          } else if (this.state.selectedTreeNode) {
            treeList.map(node => {
              if (node.uid === this.state.selectedTreeNode.uid) {
                this.setState({selectedTreeNode: node});
              }
            });
          }
        } else {
          Setting.showMessage("error", `${res.msg}`);
        }
      });
  };

  onLoadTreeData = (treeNode, loadList) => {
    const treeList = [...loadList];
    treeList.map((item, index) => {
      item.level = treeNode.level + 1;
    });
    const list = this.updateKeyNodeData(this.state.treeData, treeNode.uid, treeList);
    this.setState({treeData: list});
  };

  updateKeyNodeData = (list, key, children) =>
    list.map((node) => {
      if (node.uid === key) {
        node.childNum = key.childNum;
        return {...node, children};
      }
      if (node.children) {
        return {...node, children: this.updateKeyNodeData(node.children, key, children)};
      }

      return node;
    });

  onAddDepartment = (department, parentNode) => {
    this.setState({
      editDepartmentOpen: true,
      editMode: "add",
      editDepartment: department,
      editDeptParent: parentNode,
    });
  };

  onEditDepartment = (department) => {
    this.setState({
      editDepartmentOpen: true,
      editMode: "edit",
      editDepartment: department,
    });
  };

  onDelDepartment = (department) => {
    if (this.state.keyword === "") {
      // 更新树结构
      const parentNode = this.searchParentNode({children: this.state.treeData}, department.uid)[0];
      const found = this.searchSelectedNodes(parentNode.children);
      const treeList = this.delNode(this.state.treeData, department.uid);

      this.setState({treeData: treeList});
      if (found?.length > 0 && found[0].uid === department.uid) {
      // 删除的部门节点是选中节点，则重置选中节点
        this.setState({selectedNodeKeys: [parentNode.uid], selectedTreeNode: parentNode});
      }
    } else {
      // 更新搜索列表
      this.getNodes(this.state.keyword);
    }
  };

  onTreeNodeSelect = (selectedKeyValues, node) => {
    this.setState({
      selectedTreeNode: node,
      selectedNodeKeys: selectedKeyValues,
    });
  };

  onAddOrganization = (organId) => {
    this.setState({
      editOrganizationOpen: true,
      organizationId: organId,
      editMode: "add",
    });
  };

  onEditOrganization = (organId, displayName) => {
    this.setState({
      editOrganizationOpen: true,
      organizationId: organId,
      organizationDisplayName: displayName,
      editMode: "edit",
    });
  };

  onDelOrganization = (organization) => {
    if (this.state.keyword === "") {
      // 更新树结构
      const searchTreeList = this.state.treeData.filter(node => {
        return node.uid === organization.uid;
      });
      const found = this.searchSelectedNodes(searchTreeList);
      const treeList = this.delNode(this.state.treeData, organization.uid);

      this.setState({treeData: treeList});
      if (found?.length > 0 && treeList.length > 0) {
        // 删除的组织节点是选中节点，则重置选中节点
        this.setState({selectedNodeKeys: [treeList[0].uid], selectedTreeNode: treeList[0]});
      }
    } else {
      // 更新搜索列表
      this.getNodes(this.state.keyword);
    }
  };

  onAddUser = (user) => {
    this.setState({
      editUserOpen: true,
      editUser: user,
      editMode: "add",
    });
  };

  onEditUser = (user) => {
    this.setState({
      editUserOpen: true,
      editUser: user,
      editMode: "edit",
    });
  };

  delNode = (treeData, uid, found = null) => {
    return treeData.filter(node => {
      if (node.children) {
        node.children = this.delNode(node.children, uid, found);
        node.childNum = node.children.length;
      }
      return node.uid !== uid;
    });
  };

  searchParentNode = (root = {}, uid, found = []) => {
    if (root.children) {
      root.children.forEach(node => {
        if (node.uid === uid) {
          found.push(root);
          return found;
        }
        this.searchParentNode(node, uid, found);
      });
    }
    return found;
  };

  searchSelectedNodes = (treeData) => {
    if (this.state.selectedNodeKeys.length > 0) {
      return this.searchNodes(treeData, this.state.selectedNodeKeys[0]);
    }
    return [];
  };

  searchNodes = (treeData, uid, found = []) => {
    treeData.forEach(node => {
      if (node.uid === uid) {
        found.push(node);
        return found;
      }
      if (node.children) {
        this.searchNodes(node.children, uid, found);
      }
    });
    return found;
  };

  getSelectedNode = () => {
    const found = this.searchSelectedNodes(this.state.treeData);
    return found.length > 0 ? found[0] : null;
  };

  onFinishedSubmitOrg = (organization) => {
    this.setState({editOrganizationOpen: false});
    if (this.state.keyword === "") {
      this.fetchTreeData();
    } else {
      this.getNodes(this.state.keyword);
    }
  };

  onFinishedSubmitUser = (del) => {
    this.setState({editUserOpen: false});
  };

  onSubmitDepartment = (mode) => {
    if (this.state.keyword === "") {
      // 更新树结构
      let editDeptParent;
      if (this.state.editMode === "edit") {
        editDeptParent = this.searchParentNode({children: this.state.treeData}, this.state.editDepartment.uid)[0];
      } else {
        editDeptParent = this.state.editDeptParent;
      }
      OrganizationBackend.getDepartments(Setting.Admin,
        editDeptParent?.isOrganization ? editDeptParent.uid : editDeptParent.organId,
        editDeptParent?.isOrganization ? "0" : editDeptParent.uid)
        .then((res) => {
          if (res.msg === undefined) {
            const treeList = res.reverse();
            treeList.map((item, index) => {
              item.level = editDeptParent.level + 1;
              if (editDeptParent.children) {
                editDeptParent.children.map(node => {
                  if (item.uid === node.uid) {
                    item.children = node.children;
                  }
                });
              }
            });
            editDeptParent.childNum = treeList.length;
            if (this.state.selectedTreeNode) {
              treeList.map(node => {
                if (node.uid === this.state.selectedTreeNode.uid) {
                  this.setState({selectedTreeNode: node});
                }
              });
            }
            const list = this.updateKeyNodeData(this.state.treeData, editDeptParent.uid, treeList);
            this.setState({treeData: list});
          } else {
            Setting.showResponseMessage(res.msg);
          }
        });
    } else {
      this.getNodes(this.state.keyword);
    }
  };

  getNodes(keyword) {
    OrganizationBackend.getNodes(Setting.Admin, keyword)
      .then(res => {
        if (res.msg === undefined) {
          this.setState({searchList: res, treeData: []});
          if (res.length > 0) {
            this.setState({
              selectedSearch: res[0],
            });
          } else {
            this.setState({
              selectedSearch: null,
            });
          }
        } else {
          Setting.showResponseMessage(res.msg);
        }
      });
  }

  searchkey = (value) => {
    this.setState({
      keyword: value,
    });
    if (this.state.keyword !== "" && value === "") {
      this.setState({
        selectedSearch: null,
        searchList: [],
      });
      this.fetchTreeData(true);
    } else if (value !== "") {
      this.getNodes(value);
    }
  };

  onSelectSearch = (node) => {
    this.setState({
      selectedSearch: node,
    });
  };

  render() {
    if (this.state.editOrganizationOpen) {
      return <Row style={{height: "100%"}}>
        <Col span={24} >
          <OrganizationEditPage
            account={this.props.account}
            organizationId={this.state.organizationId}
            displayName={this.state.organizationDisplayName}
            mode={this.state.editMode}
            onFinishedSubmit={this.onFinishedSubmitOrg}
            onDeleteOrganization={() => {this.setState({editOrganizationOpen: false});}} />
        </Col>
      </Row>;
    }
    if (this.state.editUserOpen) {
      return <Row style={{height: "100%"}}>
        <Col span={24} >
          <UserEditPage account={this.props.account}
            organizationId={this.state.editUser.owner}
            userName={this.state.editUser.name}
            mode={this.state.editMode}
            onFinishedSubmit={this.onFinishedSubmitUser}
            onDeleteUser={() => {this.setState({editUserOpen: false});}} />
        </Col>
      </Row>;
    }
    return (
      <>
        <Layout hasSider style={{backgroundColor: "#fff"}}>
          <Sider
            style={{
              overflow: "auto",
              height: "calc(100vh - 130px)",
              backgroundColor: "#fff",
              borderRight: "solid 1px #ECECEC",
              paddingRight: "5px",
            }}
            width="276px"
          >
            <OrganizationTree
              treeData={this.state.treeData}
              expandedNodeKeys={this.state.expandedNodeKeys}
              selectedNodeKeys={this.state.selectedNodeKeys}
              onTreeNodeExpand={(expandedKeysValue) => {this.setState({expandedNodeKeys: expandedKeysValue});}}
              onTreeNodeSelect={this.onTreeNodeSelect}
              onLoadTreeData={this.onLoadTreeData}
              onAddDepartment={this.onAddDepartment}
              onEditDepartment={this.onEditDepartment}
              onDelDepartment={this.onDelDepartment}
              onEditOrganization={this.onEditOrganization}
              onAddOrganization={this.onAddOrganization}
              onDelOrganization={this.onDelOrganization}
              account={this.props.account}
              onSearchKey={this.searchkey}
              searchList={this.state.searchList}
              onSelectSearch={this.onSelectSearch}
              selectedSearch={this.state.selectedSearch}
              keyword={this.state.keyword} />
          </Sider>
          <Layout style={{paddingLeft: 20, backgroundColor: "#fff"}}>
            <Content>
              <UserListTable
                account={this.props.account}
                nodeData={this.state.keyword === "" ? this.state.selectedTreeNode : this.state.selectedSearch}
                onImportUser={this.onImportUser}
                onImportOrganization={this.onImportOrganization}
                onAddUser={this.onAddUser}
                onEditUser={this.onEditUser}
                {...this.props} />
            </Content>
          </Layout>
        </Layout>
        <Col span={24} style={{background: "#fff"}}>
          <DepartmentEdit
            mode={this.state.editMode}
            visible={this.state.editDepartmentOpen}
            setVisible={(visible) => this.setState({editDepartmentOpen: visible})}
            dept={this.state.editDepartment}
            onSubmitDepartment={this.onSubmitDepartment} />
        </Col>
      </>
    );
  }
}

export default UserManagementPage;
